import { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Panel from '../Panel';
import TabPanel from '../Panel/TabPanel';
import NumberInput from '../Input/NumberInput';
import OutputPanelContent from './OutputPanelContent';
import OutputPanelFertilizer from './OutputPanelFertilizer';
import { modelVersions } from '../../services/constants';
import { units } from '../../services/parameters';
import StartExplanation from './StartExplanation';
import StartSettings from './StartSettings';
import StartSettingsPrint from './StartSettingsPrint';
import Toolbox from './Toolbox';
import SettingsFertilizers from './SettingsFertilizers';
import { outputColumns } from '../../services/model';
import { issetInputValue, numberFormatter } from './utils';
import { formatIonInputValue } from '../../store/helpers';
import { analysisColumnKeys } from '../../store/constants';

const round2 = numberFormatter.format;

const breakpoint = 1200;
const OutputPanel = styled(Panel)`
    min-width: 300px;
`;
const SettingsPanel = styled(Panel)`
    width: 300px;
`;
const ExplanationPanel = SettingsPanel;

const StartWrapper = styled.div`
    display: flex;
    align-items: flex-start;
`;

const SettingsWrapper = styled.div`
    display: flex;
    flex-direction: column;

    @media (max-width: ${breakpoint}px) {
        flex-direction: row;
        align-items: flex-start;
    }
`;

const MainContainer = styled.div`
    display: flex;
    align-items: flex-start;

    @media (max-width: ${breakpoint}px) {
        flex-direction: column;
    }
`;

const FormattedNumberInput = styled(NumberInput).attrs({
    inputWidth: '100%',
})`
    background: ${(props) => (props.output ? '#fcfcad' : '#eee')};
`;

// const FlexPanelWrapper = styled.div`
//     display: flex;
//     flex-direction: column;
//     justify-content: space-between;
// `;

const outputColumn = (label, data, first) => {
    return {
        label,
        width: 100,
        cellStyle: {
            borderLeft: first ? '8px solid #fff' : null,
            textAlign: 'right',
        },
        formatter: (ion) => {
            if (!data || !data.hasOwnProperty(ion)) {
                return '-';
            }
            return <FormattedNumberInput value={data[ion]} disabled output />;
        },
    };
};

export const ModelView = ({
    modelVersion,
    selectedCrop,
    selectedSubstrate,
    selectedStage,
    dropWaterValue,
    ionParameters,
    ionValues,
    setIonValue,
    useDrainAnalysis,
    modelOutput,
    modelOutputElecCorrect,
    modelOutputFertilizer,
    modelOutputFertilizerBalanced,
    visibleOutputColumns,
    showElecCorrect,
    runModel,
    runElecCorrect,
    runBalanceAcidBase,
    busy,
    alertText,
    fertilizerViewOpen,
    setFertilizerViewOpen,
    runFertilizerModel,
}) => {
    if (!ionValues) return null;

    const blurHandler = (ion, param) => (raw) => {
        setIonValue(ion, param, formatIonInputValue(raw));
    };

    const inputColumn = (key, label) => {
        return {
            label: label || key,
            width: 100,
            formatter: (ion, row, column) => {
                if (!ionValues[ion].hasOwnProperty(key)) {
                    return '';
                }
                return (
                    <FormattedNumberInput
                        value={ionValues[ion][key]}
                        onChange={(val) => {
                            setIonValue(ion, key, val);
                        }}
                        onBlur={blurHandler(ion, key)}
                        tabIndex={100 * column + row}
                    />
                );
            },
        };
    };

    let ionTableColumns = [{ label: '', formatter: (ion) => <b>{ion}</b> }];

    if (!fertilizerViewOpen) {
        ionTableColumns.push(
            inputColumn(
                'feedWaterAnalysis',
                <span>
                    Schoonwater
                    <wbr />
                    analyse
                </span>
            )
        );

        if (modelVersion === modelVersions.potplant) {
            if (useDrainAnalysis) {
                ionTableColumns.push(
                    inputColumn(
                        'drainAnalysis',
                        <span>
                            Drain
                            <wbr />
                            analyse
                        </span>
                    )
                );
            } else {
                ionTableColumns.push(
                    inputColumn(
                        analysisColumnKeys.oneToOneDotFive,
                        '1:1,5 analyse'
                    )
                );
            }
        }
        if (modelVersion === modelVersions.substraat) {
            ionTableColumns.push(inputColumn('drainAnalysis', 'Drainanalyse'));
        }
    }

    let hasOutput = false;

    if (modelOutput && visibleOutputColumns?.length) {
        if (fertilizerViewOpen) {
            let fertilizerSourceRecipe;
            let columnLabel = null;
            if (showElecCorrect && modelOutputElecCorrect) {
                fertilizerSourceRecipe = modelOutputElecCorrect;
                columnLabel = 'Gecorrigeerd recept';
            } else if (modelOutput[outputColumns.advies]) {
                fertilizerSourceRecipe = modelOutput[outputColumns.advies];
                columnLabel = 'Recept';
            }
            hasOutput = Boolean(fertilizerSourceRecipe);
            if (hasOutput) {
                ionTableColumns.push({
                    label: columnLabel,
                    width: 100,
                    cellStyle: {
                        // borderLeft: first ? '8px solid #fff' : null,
                        textAlign: 'right',
                    },
                    formatter: (ion) => {
                        if (!fertilizerSourceRecipe.hasOwnProperty(ion)) {
                            return '-';
                        }
                        return (
                            <FormattedNumberInput
                                value={fertilizerSourceRecipe[ion]}
                                disabled
                                output
                            />
                        );
                    },
                });
            }

            // The fertilizer model output:
            if (modelOutputFertilizer) {
                ionTableColumns.push({
                    label: 'model recept',
                    width: 100,
                    cellStyle: {
                        textAlign: 'right',
                    },
                    formatter: (ion) => {
                        if (
                            !modelOutputFertilizer.recipe_mols.hasOwnProperty(
                                ion
                            )
                        ) {
                            return '-';
                        }
                        return (
                            <FormattedNumberInput
                                value={round2(
                                    modelOutputFertilizer.recipe_mols[ion]
                                )}
                                disabled
                                output
                            />
                        );
                    },
                });
            }

            // The balanced fertilizer model output:
            if (modelOutputFertilizerBalanced) {
                ionTableColumns.push({
                    label: 'gebalanceerd recept',
                    width: 100,
                    cellStyle: {
                        textAlign: 'right',
                    },
                    formatter: (ion) => {
                        if (
                            !modelOutputFertilizerBalanced.recipe_mols.hasOwnProperty(
                                ion
                            )
                        ) {
                            return '-';
                        }
                        return (
                            <FormattedNumberInput
                                value={round2(
                                    modelOutputFertilizerBalanced.recipe_mols[
                                        ion
                                    ]
                                )}
                                disabled
                                output
                            />
                        );
                    },
                });
            }
        } else {
            let first = true;
            visibleOutputColumns.forEach(({ dataKey, label }) => {
                const columnOutput = modelOutput[dataKey];
                if (columnOutput) {
                    ionTableColumns.push(
                        outputColumn(label, columnOutput, first)
                    );
                    first = false;
                }
            });

            hasOutput = !first;

            if (modelOutput[outputColumns.advies] && showElecCorrect) {
                const adviesData = modelOutput[outputColumns.advies];
                ionTableColumns.push(
                    // inputColumn('elecCorrectRecipe', 'Aangepast recept')
                    {
                        label: 'Aangepast recept',
                        width: 100,
                        formatter: (ion, row, column) => {
                            if (
                                !ionValues[ion].hasOwnProperty(
                                    'elecCorrectRecipe'
                                )
                            ) {
                                return '';
                            }
                            return (
                                <FormattedNumberInput
                                    style={
                                        ionValues[ion]['elecCorrectRecipe'] ===
                                        adviesData[ion]
                                            ? {}
                                            : {
                                                  fontWeight: 'bold',
                                                  backgroundColor: '#fff',
                                              }
                                    }
                                    value={ionValues[ion]['elecCorrectRecipe']}
                                    onChange={(val) => {
                                        setIonValue(
                                            ion,
                                            'elecCorrectRecipe',
                                            val
                                        );
                                    }}
                                    onBlur={blurHandler(
                                        ion,
                                        'elecCorrectRecipe'
                                    )}
                                    tabIndex={100 * column + row}
                                />
                            );
                        },
                    }
                );
                if (modelOutputElecCorrect) {
                    ionTableColumns.push({
                        label: 'Gecorrigeerd recept',
                        width: 100,
                        cellStyle: {
                            // borderLeft: first ? '8px solid #fff' : null,
                            textAlign: 'right',
                        },
                        formatter: (ion) => {
                            if (!modelOutputElecCorrect.hasOwnProperty(ion)) {
                                return '-';
                            }
                            return (
                                <FormattedNumberInput
                                    value={modelOutputElecCorrect[ion]}
                                    disabled
                                    output
                                />
                            );
                        },
                    });
                }
            }
        }
    }

    ionTableColumns.push({
        label: 'unit',
        width: 50,
        formatter: (ion) => {
            if (!units || !units.hasOwnProperty(ion)) {
                return '';
            }
            return units[ion];
        },
    });

    const mainSettingsComplete =
        // !fertilizerViewOpen &&
        selectedCrop &&
        selectedStage &&
        issetInputValue(dropWaterValue) &&
        ((modelVersion === modelVersions.substraat && selectedSubstrate) ||
            modelVersion === modelVersions.potplant);
    const showTable = mainSettingsComplete;

    return (
        <MainContainer>
            <SettingsWrapper>
                <StartWrapper>
                    <Panel width={300}>
                        <StartSettings
                            modelVersion={modelVersion}
                            className="dont-print"
                        />
                        <StartSettingsPrint
                            modelVersion={modelVersion}
                            className="only-print"
                        />
                    </Panel>

                    {Boolean(!mainSettingsComplete) && (
                        <ExplanationPanel className="dont-print">
                            <StartExplanation modelVersion={modelVersion} />
                        </ExplanationPanel>
                    )}

                    <div className="only-print"></div>
                </StartWrapper>

                {mainSettingsComplete && hasOutput && (
                    <TabPanel
                        width={300}
                        tabs={['adviesrecept', 'meststoffen']}
                        activeTab={
                            fertilizerViewOpen ? 'meststoffen' : 'adviesrecept'
                        }
                        setActiveTab={(tab) =>
                            setFertilizerViewOpen(tab === 'meststoffen')
                        }
                        className="dont-print"
                    >
                        <Toolbox
                            hasOutput={true}
                            runModel={runModel}
                            runElecCorrect={runElecCorrect}
                            busy={busy}
                            alertText={alertText}
                            component={Fragment}
                            openFertilizerView={() =>
                                setFertilizerViewOpen(true)
                            }
                        />
                        <SettingsFertilizers
                            busy={busy}
                            alertText={alertText}
                            component={Fragment}
                            runModel={runFertilizerModel}
                            runBalanceAcidBase={runBalanceAcidBase}
                            hasOutput={Boolean(modelOutputFertilizer)}
                        />
                    </TabPanel>
                )}
            </SettingsWrapper>

            {showTable && (
                <OutputPanel>
                    {mainSettingsComplete && hasOutput && fertilizerViewOpen ? (
                        <OutputPanelFertilizer
                            ionTableColumns={ionTableColumns}
                            ionParameters={ionParameters}
                            fertilizerData={modelOutputFertilizer}
                            balancedFertilizerData={
                                modelOutputFertilizerBalanced
                            }
                        />
                    ) : (
                        <OutputPanelContent
                            ionTableColumns={ionTableColumns}
                            ionParameters={ionParameters}
                            ionValues={ionValues}
                            runModel={runModel}
                            busy={busy}
                            hasOutput={hasOutput}
                            alertText={alertText}
                        />
                    )}
                </OutputPanel>
            )}
        </MainContainer>
    );
};
ModelView.propTypes = {
    selectedCrop: PropTypes.string, // the name (identifier) of the selected crop
    selectedSubstrate: PropTypes.string, // the name (identifier) of the selected substrate
    selectedStage: PropTypes.string,
};

export default ModelView;
